import type { AppDataConfig } from '@/types';
import * as v from 'valibot';
import { getDateFromToday } from '@/components/calendar/utils';
import { store } from '@/redux/store';
import { selectAnswersDefaultByKey } from '@/redux/slider';
import Condition from '@/utils/condition';
import FormSchema from '@/utils/form-schema';

const allSlides = (key: string) => selectAnswersDefaultByKey(key)(store.getState());

const appData = {
  page: 'h4w-h4w',
  googleLogin: false,
  dateFormatToSend: {
    bewilligungsdatum: 'YYYY-MM-DD',
    bescheidablaufdatum: 'YYYY-MM-DD',
  },
  externalIdGenerateMethod: 'sha1',
  iconPosition: 'after',
  cssVersion: 1,
  dontLookForSecret: true,
  header: {
    showAlert: false,
    alertMessage:
      '<b>Aktuelle Information</b>: Trotz Corona-Krise steht Ihnen unser Bescheidcheck wie gewohnt zur Verfügung.',
    alertCss: {
      background: '#eff5f4',
      color: '#302576',
    },
    info: '',
    phone: '',
    openingTimes: '',
    enableStickyMobileHead: false,
  },
  banner: {
    title: [
      {
        condition: () => true,
        value: {
          xxxs: '<span style="font-weight:500;">Bürgergeld-Bescheid</span> erhalten:<br>Steht Ihnen <span style="font-weight:500;">mehr Geld</span> zu?',
          xxs: '<span style="font-weight:500;">Bürgergeld-Bescheid</span> erhalten:<br>Steht Ihnen <span style="font-weight:500;">mehr Geld</span> zu?',
          xs: '<span style="font-weight:500;">Bürgergeld-Bescheid</span> erhalten:<br>Steht Ihnen <span style="font-weight:500;">mehr Geld</span> zu?',
          sm: '<span style="font-weight:500;">Bürgergeld-Bescheid</span> erhalten:<br>Steht Ihnen <span style="font-weight:500;">mehr Geld</span> zu?',
          md: '<span style="font-weight:500;">Bürgergeld-Bescheid</span> erhalten:<br>Steht Ihnen <span style="font-weight:500;">mehr Geld</span> zu?',
          lg: '<span style="font-weight:500;">Bürgergeld-Bescheid</span> erhalten:<br>Steht Ihnen <span style="font-weight:500;">mehr Geld</span> zu?',
        },
      },
    ],
    subtitle: [
      {
        condition: () => true,
        value: 'Lassen Sie jetzt kostenlos Ihren Bescheid prüfen',
      },
    ],
  },
  sign: {
    withEnvelopIcon: false,
    withEmojies: true,
    titleSuccessColor: false,
    congratulation: [
      {
        condition: () => true,
        value: {
          title: 'Wir können Ihren Bescheid prüfen!',
          content: 'Um die kostenlose Prüfung zu starten, benötigen wir Ihre Kontaktdaten:',
        },
      },
    ],
    form: {
      schema: (keys) =>
        v.object({
          salutation: FormSchema.salutation(keys, ['Herr', 'Frau', 'Divers']),
          first_name: FormSchema.firstName(keys),
          last_name: FormSchema.lastName(keys),
          email: FormSchema.email(keys),
          phone: FormSchema.phone(keys),
        }),
      layout: [
        {
          label: 'Anrede',
          cells: [
            {
              type: 'radio',
              key: 'salutation',
              choices: [
                {
                  label: 'Herr',
                  value: 'Herr',
                  isDefault: true,
                },
                {
                  label: 'Frau',
                  value: 'Frau',
                },
                {
                  label: 'Divers',
                  value: 'Divers',
                },
              ],
            },
          ],
        },
        {
          label: 'Name',
          cells: [
            {
              type: 'text',
              key: 'first_name',
              label: 'Vorname',
              placeholder: 'Vorname',
              placeholderShort: 'Vorname',
            },
            {
              type: 'text',
              key: 'last_name',
              label: 'Nachname',
              placeholder: 'Nachname',
              placeholderShort: 'Nachname',
            },
          ],
        },
        {
          label: 'E-Mail',
          cells: [
            {
              type: 'text',
              key: 'email',
              label: 'E-Mail',
              placeholder: 'z.B. max@mustermann.de',
              placeholderShort: 'z.B. max@mustermann.de',
            },
          ],
        },
        {
          label: 'Telefon',
          cells: [
            {
              type: 'text',
              key: 'phone',
              label: 'Telefon',
              placeholder: 'z.B. +49 170 000 000',
              placeholderShort: 'z.B. +49 170 000 000',
            },
          ],
        },
      ],
      button: [
        {
          condition: () => true,
          value: 'Weiter zum Upload',
        },
      ],
    },
    certificates: {
      hints: ['Garantierter Datenschutz - 100% Kostenlos'],
      withTuv: false,
    },
  },
  article: null,
  keywordWhiteList: [],
  selectedKeywordTitle: 'hartz4widerspruch.de',
  domainName: 'www.hartz4widerspruch.de',
  isInternationalizationOn: false,
  prefix: [
    {
      condition: () => true,
      value: 'H4W',
    },
  ],
  switchSlidesMode: 'on-next',
  questions: [
    {
      title: 'Um welchen Bescheid handelt es sich?',
      key: 'bescheidtyp',
      type: 'multiple',
      subline: 'Die Art Ihres Bescheides steht fett gedruckt in der Überschrift.',
      isRoot: true,
      choices: [
        {
          value: 'Bewilligungsbescheid',
          label: 'Bewilligungs&shybescheid',
          icon: 'bewilligung',
          isPreselected: true,
          path: ['bewilligungsdatum', 'zip', 'formular'],
        },
        {
          value: 'Änderungsbescheid',
          label: 'Änderungs&shybescheid',
          icon: 'aenderung',
          path: ['bewilligungsdatum', 'zip', 'formular'],
        },
        {
          value: 'Ablehnungsbescheid',
          label: 'Ablehnungs&shybescheid',
          icon: 'ablehnung',
          path: ['bewilligungsdatum', 'zip', 'formular'],
        },
        {
          value: 'Sonstiger Bescheid',
          label: 'Sonstiger Bescheid',
          icon: 'sonstiges',
          path: ['sonstigerbescheidtyp', 'bewilligungsdatum', 'zip', 'formular'],
        },
        {
          value: 'Undefiniert',
          label: 'Kein Bescheid / Unsicher',
          icon: 'unsicher',
          path: ['bewilligungsdatum', 'zip', 'formular'],
        },
      ],
    },
    {
      title: 'Wählen Sie Ihren Bescheidtyp aus dieser Liste:',
      key: 'sonstigerbescheidtyp',
      type: 'dropdown',
      overwrites: 'bescheidtyp',
      isSearchHidden: false,
      placeholder: 'Wählen Sie den Bescheidtyp aus ...',
      noResult: 'Ihr Bescheidtyp wurde nicht gefunden.',
      subline: 'Leer lassen wenn unsicher.',
      defaultValue: 'Keine Angabe / Unsicher',
      choices: [
        {
          label: 'Vorläufiger Bewilligungsbescheid',
          value: 'Vorläufiger Bewilligungsbescheid',
        },
        {
          label: 'Vorläufiger Änderungsbescheid',
          value: 'Vorläufiger Änderungsbescheid',
        },
        {
          label: 'Aufhebung- und Erstattungsbescheid',
          value: 'Aufhebung- und Erstattungsbescheid',
        },
        {
          label: 'Erstattung von Leistungen bei endgültiger Festsetzung des Leistungsanspruches',
          value: 'Erstattung von Leistungen bei endgültiger Festsetzung des Leistungsanspruches',
        },
        {
          label: 'Bescheid zur endgültigen Festsetzung von Leistungen',
          value: 'Bescheid zur endgültigen Festsetzung von Leistungen',
        },
        {
          label: 'Sanktionsbescheid',
          value: 'Sanktionsbescheid',
        },
        {
          label: 'Aufhebungsbescheid',
          value: 'Aufhebungsbescheid',
        },
        {
          label: 'Mahnbescheid',
          value: 'Mahnbescheid',
        },
        {
          label: 'Eingliederungsvereinbarung (EGV)',
          value: 'Eingliederungsvereinbarung (EGV)',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Bescheid zur Einstellung der Leistungen',
          value: 'Bescheid zur Einstellung der Leistungen',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Aufrechnungsbescheid',
          value: 'Aufrechnungsbescheid',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Versagungsbescheid',
          value: 'Versagungsbescheid',
        },
        {
          label: 'Bewilligung von einmaligen Leistungen',
          value: 'Bewilligung von einmaligen Leistungen',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Darlehensbescheid',
          value: 'Darlehensbescheid',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Rücknahme- und Erstattungsbescheid',
          value: 'Rücknahme- und Erstattungsbescheid',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Entziehungsbescheid',
          value: 'Entziehungsbescheid',
        },
        {
          label: 'Anhörung',
          value: 'Anhörung',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Aufforderung zur Mitwirkung',
          value: 'Aufforderung zur Mitwirkung',
          path: ['kein-check-bescheid'],
        },
        {
          label: 'Einladung zum Termin',
          value: 'Einladung zum Termin',
          path: ['kein-check-bescheid'],
        },
      ],
    },
    {
      title: 'Wie lautet das Bescheiddatum?',
      key: 'bewilligungsdatum',
      type: 'datefield',
      calendarMode: 'dropdown',
      calendarPrecision: 'day',
      calendarModalPosition: 'static',
      calendarPeriod: ['no limit', 'today'],
      finalValue: getDateFromToday(),
      enableNotSureCheckbox: false,
      notSureLabel: 'Ich bin mir nicht sicher',
      notSureValue: '',
      subline: 'Das Datum finden Sie auf Ihrem Bescheid auf der ersten Seite oben rechts.',
      path: [
        {
          condition: () => Condition.date(allSlides('bewilligungsdatum')).less(getDateFromToday(-35)),
          value: ['kein-check'],
        },
        {
          condition: () => Condition.date(allSlides('bewilligungsdatum')).moreOrEqual(getDateFromToday(-35)),
          value: ['zip', 'formular'],
        },
      ],
      validate: {
        required: false,
        fn: (v) =>
          Condition.messages(
            [Condition.isEmpty(v), 'Geben Sie bitte zumindest das ungefähre Datum an.'],
            [Condition.isNotInPeriod(v, ['no limit', 'today']), 'Das Datum muss in der Vergangenheit liegen.'],
          ),
      },
    },
    {
      title: 'Wie lautet das Ende des Bewilligungszeitraums?',
      key: 'bescheidablaufdatum',
      type: 'datefield',
      calendarMode: 'dropdown',
      calendarPrecision: 'day',
      calendarModalPosition: 'static',
      calendarPeriod: ['today', 'no limit'],
      finalValue: getDateFromToday(),
      enableNotSureCheckbox: false,
      notSureLabel: 'Ich bin mir nicht sicher',
      notSureValue: '',
      subline: 'Das Ende Ihres Bewilligungszeitraums finden Sie meistens im ersten Absatz Ihres Bescheides.',
      validate: {
        required: false,
        fn: (v) => {
          return Condition.messages(
            [Condition.isEmpty(v), 'Geben Sie bitte zumindest das ungefähre Datum an.'],
            [Condition.isNotInPeriod(v, ['today', 'no limit']), 'Das Datum muss in der Zukunft liegen.'],
          );
        },
      },
    },
    {
      title: '',
      key: 'keine-hilfe',
      label: 'keine-hilfe',
      type: 'paragraph',
      disableButtons: ['next'],
      defaultValue: '0',
      content:
        '<div><h2>Es tut uns leid. Prüfung leider nicht möglich.</h2><p>Unsere Partnerkanzlei kann Sie nur unterstützen, wenn ein konkreter Bescheid zur Prüfung vorliegt.</p><p>Sobald Ihnen ein entsprechender Bescheid vorliegt, kommen Sie gerne erneut auf uns zu.</p></div>',
    },
    {
      title: '',
      key: 'kein-check',
      label: 'kein-check',
      type: 'paragraph',
      disableButtons: ['next', 'prev'],
      defaultValue: '0',
      content:
        '<div><h2>Es tut uns leid. Prüfung leider nicht möglich.</h2><p>Die Widerspruchsfrist ist bereits abgelaufen.</p><p>Leider können wir nur Bescheide innerhalb einer Frist von <b>max. 30 Tagen nach Erhalt</b> prüfen.</p><p>Sie können jedoch auch nach Ablauf der Widerspruchsfrist beim Jobcenter einen <b>Überprüfungsantrag</b> einreichen.</p><p>Das dafür notwendige Formular sowie Tipps zum Vorgehen finden Sie hier:</p><div class="slider-type-navigation-container static-button" style="margin-top: 24px; margin-bottom: 80px"><div class="slider-type-navigation-button-wrapper" style="text-align: center;"><a href="https://hartz4widerspruch.de/antraege/ueberpruefungsantrag/" class="btn button-next-bewilligungsdatum btn--primary btn--arrow btn--after">Zum Überprüfungsantrag</a></div></div></div>',
    },
    {
      title: '',
      key: 'kein-check-bescheid',
      label: 'kein-check-bescheid',
      type: 'paragraph',
      disableButtons: ['next'],
      defaultValue: '0',
      content:
        '<div><h2>Es tut uns leid. Prüfung leider nicht möglich.</h2><p>Sie haben sich mit dem sozialrechtlichen Anliegen einer Bescheidprüfung an uns gewendet.</p><p>Leider kann Sie unsere Partnerkanzlei bei der spezifischen Anfrage zur Zeit nicht beraten. Wir bedauern, Sie in der Angelegenheit nicht unterstützen zu können.</p></div>',
    },
    {
      title: 'Bitte geben Sie Ihre Postleitzahl ein:',
      key: 'zip',
      type: 'text',
      defaultValue: '',
      placeholder: 'z.B. 12345',
      subline: '5-stellige Postleitzahl Ihres Wohnsitzes',
    },
    {
      title: '',
      key: 'formular',
      type: 'submission',
    },
  ],
  resultPage: {
    success: () => (
      <>
        <h3 className='h3'>Vielen Dank für Ihre kostenlose Anfrage bei hartz4widerspruch</h3>
        <p className='p'>
          Im nächsten Schritt können Sie uns eine Vollmacht erteilen und uns Ihren Bescheid zur kostenlosen Prüfung
          übermitteln.
        </p>
        <div className='slider-type-navigation-container static-button' style={{ marginTop: 24, marginBottom: 80 }}>
          <div className='slider-type-navigation-button-wrapper'>
            <a
              href='https://hartz4widerspruch.de/vollmacht/?secret={externalId}'
              className='btn button-next-bewilligungsdatum btn--primary btn--arrow btn--after'
            >
              Zur Online-Vollmacht
            </a>
          </div>
        </div>
      </>
    ),
    failure: () => (
      <>
        <h3 className='h3'>OH NEIN!! Irgendwas ist schief gelaufen!</h3>
        <p className='p'>
          Rufen Sie uns bitte unter der 030 / 20 89 81 250 an oder schreiben Sie uns eine E-Mail an
          <a className='a' href='mailto:pkv@rechtsanwalt-ruff.de'>
            pkv@rechtsanwalt-ruff.de
          </a>
          , damit wir Ihnen weiterhelfen können.
        </p>
      </>
    ),
  },
  additionalQuestions: null,
  certificateLogos: ['tls', 'tuv'],
  showPriceModal: 0,
  showPriceModalInsuranceCondition: {},
  appendPoaToCTA: false,
  via: 'L1 Funnel - H4W - v3',
  statics: {
    agb_version: 'einwilligung-20190617',
  },
  newClientPercentToShow: 0,
  useDynamicLandingPage: false,
  dynamicLandingPageVersion: 19,
  percentDynamicLandingPageOnPrioInsideOfficeHours: null,
  percentDynamicLandingPageOnPrioOutSideOfficeHours: null,
  openHours: null,
  callDelayMinutes: 1,
  useAlternateSuccessPage: false,
  alternateSuccessPageVersion: 19,
  useInsoPriceRandomizer: false,
  insoPriceVariations: null,
  insoPriceDefault: '',
  useThanksPageWithCalculation: false,
  calculationPageUrlName: '',
  fidMapping: null,
  enableUploadFiles: true,
  redirectToOnSubmit: 'https://hartz4widerspruch.de/vollmacht/?secret={externalId}',
  vollmachtApiUrl: 'https://api.jobcenter-bescheid-check.de/api/files',
  usePrioCalculatorV3: 0,
  usePrioCalculatorV2: 0,
  footerDomainName: '',
  onSuccessPhpResolve: null,
} satisfies AppDataConfig as AppDataConfig;

export default appData;
